import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Check as SaveIcon,
  Close as CancelIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import debounce from 'lodash/debounce';
import { useVenue } from '../../../../context/VenueContext';
import purchaseOrderService from '../../../../services/purchaseOrderService';
import supplierService from '../../../../services/supplierService';
import inventoryItemService from '../../../../services/inventoryItemService';
import venueLocationService from '../../../../services/venueLocationService';
import { getPrefill, getLinksBySupplier } from '../../../../services/supplierItemLinkService';
import type { InventoryItemWithBalance } from '../../../../services/inventoryItemService';
import type { PurchaseOrder, PurchaseOrderLine, Supplier, VenueLocation } from '../../../../types/venue';
import { formatPoLineSize } from '../../../../utils/poLineSize';

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Size string for a full item. */
const formatItemSize = (item: { sizeValue?: number | null; sizeUnit?: string | null }): string => {
  if (item.sizeValue != null) return `${item.sizeValue} ${item.sizeUnit ?? 'ml'}`;
  return '';
};

const buildDisplayName = (item: InventoryItemWithBalance): string => item.displayName;

// ── Add Line Dialog ────────────────────────────────────────────────────────────

interface AddLineDialogProps {
  open: boolean;
  onClose: () => void;
  onAdded: () => void;
  poId: string;
  venueId: string;
  supplierId: string;
  locations: VenueLocation[];
  existingInventoryItemIds: Set<string>;
  supplierCodeMap: Map<string, string>;
}

const AddLineDialog: React.FC<AddLineDialogProps> = ({
  open, onClose, onAdded, poId, venueId, supplierId, locations, existingInventoryItemIds, supplierCodeMap,
}) => {
  const theme = useTheme();

  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState<InventoryItemWithBalance[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedItem, setSelectedItem] = useState<InventoryItemWithBalance | null>(null);
  const [qty, setQty] = useState('1');
  const [price, setPrice] = useState('');
  const [priceTouched, setPriceTouched] = useState(false);
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);

  // Debounce search
  const applySearch = useMemo(
    () => debounce((q: string) => setSearchQuery(q), 350),
    [],
  );
  useEffect(() => () => applySearch.cancel(), [applySearch]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
    applySearch(e.target.value);
    setSelectedItem(null);
    setAddError(null);
  };

  // Run search
  useEffect(() => {
    if (!open || !venueId) return;
    if (!searchQuery.trim()) { setResults([]); return; }
    setSearching(true);
    inventoryItemService.getItems(venueId, 1, 20, 'displayName', 'asc', searchQuery)
      .then(r => setResults(r.data ?? []))
      .catch(() => setResults([]))
      .finally(() => setSearching(false));
  }, [open, venueId, searchQuery]);

  // Reset when dialog opens
  useEffect(() => {
    if (open) {
      setSearchInput('');
      setSearchQuery('');
      setResults([]);
      setSelectedItem(null);
      setQty('1');
      setPrice('');
      setPriceTouched(false);
      setAdding(false);
      setAddError(null);
      setTouched(false);
    }
  }, [open]);

  const handleSelect = (item: InventoryItemWithBalance) => {
    setSelectedItem(item);
    setPriceTouched(false);

    // Prefill from supplier memory; fall back to InventoryBalance.lastCost
    const balanceFallback = item.balance?.lastCost != null ? String(item.balance.lastCost) : '';
    setPrice(balanceFallback);

    if (supplierId) {
      getPrefill(venueId, item._id, supplierId).then(result => {
        if (result.suggestedCost !== null) {
          // Only apply if operator hasn't typed in the field since item was selected
          setPriceTouched(prev => {
            if (!prev) setPrice(String(result.suggestedCost));
            return prev;
          });
        }
      }).catch(() => { /* silent — balance fallback already set */ });
    }

    setAddError(null);
  };

  const qtyNum = parseInt(qty, 10);
  const qtyValid = !isNaN(qtyNum) && qtyNum >= 1 && Number.isInteger(qtyNum);
  const priceNum = price !== '' ? parseFloat(price) : undefined;

  const handleAdd = async () => {
    setTouched(true);
    if (!selectedItem || !qtyValid) return;
    setAdding(true);
    setAddError(null);
    try {
      // displayName, bottleSize, and locationName are generated server-side from InventoryItem.
      await purchaseOrderService.createLine(poId, {
        inventoryItemId: selectedItem._id,
        orderedQty: qtyNum,
        ...(priceNum !== undefined && { unitPrice: priceNum }),
      });
      onAdded();
      // Reset form for another line (keep dialog open)
      setSearchInput('');
      setSearchQuery('');
      setResults([]);
      setSelectedItem(null);
      setQty('1');
      setPrice('');
      setPriceTouched(false);
      setTouched(false);
    } catch (err: any) {
      const status = err?.response?.status;
      const code = err?.response?.data?.code;
      const msg = err?.response?.data?.message;
      if (status === 409 || code === 'DUPLICATE_LINE') {
        setAddError('This inventory item is already on this purchase order.');
      } else {
        setAddError(msg ?? err?.message ?? 'Failed to add line.');
      }
    } finally {
      setAdding(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Add Order Line</DialogTitle>
      <DialogContent sx={{ pt: '8px !important', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Search */}
        <TextField
          label="Search inventory items"
          value={searchInput}
          onChange={handleSearchChange}
          placeholder="Search items…"
          size="small"
          fullWidth
          autoFocus
          slotProps={{
            input: {
              startAdornment: <SearchIcon sx={{ color: 'text.disabled', mr: 0.5, fontSize: 18 }} />,
              endAdornment: searching ? <CircularProgress size={16} /> : null,
            },
          }}
        />

        {/* Results */}
        {searchQuery && !searching && results.length === 0 && (
          <Alert severity="info" sx={{ py: 0.5 }}>
            No inventory items found for "{searchQuery}". Items must exist in inventory before they can be ordered.
          </Alert>
        )}

        {results.length > 0 && (
          <Paper variant="outlined" sx={{ maxHeight: 240, overflow: 'auto' }}>
            <List dense disablePadding>
              {results.map((item, idx) => {
                const alreadyOnOrder = existingInventoryItemIds.has(item._id);
                const isSelected = selectedItem?._id === item._id;
                return (
                  <React.Fragment key={item._id}>
                    {idx > 0 && <Divider component="li" />}
                    <ListItemButton
                      selected={isSelected}
                      onClick={() => !alreadyOnOrder && handleSelect(item)}
                      disabled={alreadyOnOrder}
                      sx={{
                        px: 2, py: 1,
                        '&.Mui-selected': { backgroundColor: alpha(theme.palette.primary.main, 0.08) },
                      }}
                    >
                      <ListItemText
                        primary={
                          <Typography variant="body2" fontWeight={500}>
                            {buildDisplayName(item)}
                          </Typography>
                        }
                        secondary={
                          alreadyOnOrder ? (
                            <Typography variant="caption" color="text.secondary">Already on this order</Typography>
                          ) : supplierCodeMap.get(item._id) ? (
                            <Typography variant="caption" color="text.secondary">{supplierCodeMap.get(item._id)}</Typography>
                          ) : null
                        }
                      />
                    </ListItemButton>
                  </React.Fragment>
                );
              })}
            </List>
          </Paper>
        )}

        {/* Selected item + qty/price form */}
        {selectedItem && (
          <Box sx={{ p: 2, borderRadius: 1.5, border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`, backgroundColor: alpha(theme.palette.primary.main, 0.03) }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Selected
            </Typography>
            <Typography variant="body2" fontWeight={600} mt={0.25}>
              {buildDisplayName(selectedItem)}
            </Typography>
            {supplierCodeMap.get(selectedItem._id) && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {supplierCodeMap.get(selectedItem._id)}
              </Typography>
            )}

            <Box sx={{ display: 'flex', gap: 2, mt: 1.5 }}>
              <TextField
                label="Qty *"
                value={qty}
                onChange={e => setQty(e.target.value)}
                type="number"
                size="small"
                sx={{ width: 100 }}
                error={touched && !qtyValid}
                helperText={touched && !qtyValid ? 'Must be ≥ 1' : ''}
                slotProps={{ htmlInput: { min: 1, step: 1 } }}
              />
              <TextField
                label="Unit Price"
                value={price}
                onChange={e => { setPrice(e.target.value); setPriceTouched(true); }}
                type="number"
                size="small"
                sx={{ width: 140 }}
                placeholder="Optional"
                slotProps={{ htmlInput: { min: 0, step: 0.01 } }}
              />
            </Box>
          </Box>
        )}

        {addError && <Alert severity="error" sx={{ py: 0.5 }}>{addError}</Alert>}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Done</Button>
        <Button
          variant="contained"
          onClick={handleAdd}
          disabled={!selectedItem || adding}
          startIcon={adding ? <CircularProgress size={16} /> : <AddIcon />}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          Add to Order
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Main Form ─────────────────────────────────────────────────────────────────

const PurchaseOrderForm: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { venueId, defaultCurrency } = useVenue();
  const { poId } = useParams<{ poId: string }>();
  const isCreate = !poId;

  const listPath = `/admin/venues/${venueId}/purchasing`;
  const detailPath = (id: string) => `/admin/venues/${venueId}/purchasing/${id}`;
  const editPath = (id: string) => `/admin/venues/${venueId}/purchasing/${id}/edit`;

  // ── Page load ─────────────────────────────────────────────────────────────────
  const [initLoading, setInitLoading] = useState(!isCreate);
  const [initError, setInitError] = useState<string | null>(null);

  // ── Supporting data ───────────────────────────────────────────────────────────
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [locations, setLocations] = useState<VenueLocation[]>([]);

  // ── PO state ──────────────────────────────────────────────────────────────────
  const [po, setPo] = useState<PurchaseOrder | null>(null);
  const [lines, setLines] = useState<PurchaseOrderLine[]>([]);

  // ── Supplier code map — keyed by inventoryItemId, populated once per supplier ──
  const [supplierCodeMap, setSupplierCodeMap] = useState<Map<string, string>>(new Map());

  // ── Header form fields ────────────────────────────────────────────────────────
  const [supplierId, setSupplierId] = useState('');
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState('');
  const [notes, setNotes] = useState('');
  const [headerTouched, setHeaderTouched] = useState(false);
  const [headerSaving, setHeaderSaving] = useState(false);
  const [headerError, setHeaderError] = useState<string | null>(null);
  const [headerSuccess, setHeaderSuccess] = useState(false);

  // ── Line editing state ────────────────────────────────────────────────────────
  const [editingLineId, setEditingLineId] = useState<string | null>(null);
  const [editQty, setEditQty] = useState('');
  const [editPrice, setEditPrice] = useState('');
  const [lineOpLoading, setLineOpLoading] = useState(false);
  const [lineOpError, setLineOpError] = useState<string | null>(null);

  // ── Add line dialog ───────────────────────────────────────────────────────────
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  // ── Derived ───────────────────────────────────────────────────────────────────
  const isDraft = po?.status === 'draft' || isCreate;
  const existingInventoryItemIds = useMemo(
    () => new Set(lines.map(l => l.inventoryItemId)),
    [lines],
  );

  // ── Load supporting data ──────────────────────────────────────────────────────
  useEffect(() => {
    supplierService.getAllSuppliers(1, 500)
      .then(r => setSuppliers(r.data ?? []))
      .catch(() => { /* non-blocking */ });

    if (venueId) {
      venueLocationService.getLocations(venueId)
        .then(setLocations)
        .catch(() => { /* non-blocking */ });
    }
  }, [venueId]);

  // Fetch supplier-item links once per supplier to build supplierItemCode lookup map.
  useEffect(() => {
    if (!supplierId) { setSupplierCodeMap(new Map()); return; }
    getLinksBySupplier(supplierId)
      .then(links => {
        const map = new Map<string, string>();
        for (const link of links) {
          if (link.inventoryItemId && link.supplierItemCode) {
            map.set(link.inventoryItemId, link.supplierItemCode);
          }
        }
        setSupplierCodeMap(map);
      })
      .catch(() => setSupplierCodeMap(new Map()));
  }, [supplierId]);

  // ── Load PO in edit mode ──────────────────────────────────────────────────────
  const loadLines = useCallback(async (currentPoId: string) => {
    try {
      const result = await purchaseOrderService.getLines(currentPoId);
      setLines(result.data ?? []);
    } catch {
      // non-blocking — lines section shows empty
    }
  }, []);

  useEffect(() => {
    if (isCreate || !poId) return;
    setInitLoading(true);
    Promise.all([
      purchaseOrderService.getOrder(poId),
      purchaseOrderService.getLines(poId),
    ])
      .then(([loadedPo, linesResult]) => {
        setPo(loadedPo);
        setLines(linesResult.data ?? []);
        setSupplierId(loadedPo.supplierId);
        setExpectedDeliveryDate(
          loadedPo.expectedDeliveryDate ? loadedPo.expectedDeliveryDate.split('T')[0] : '',
        );
        setNotes(loadedPo.notes ?? '');

        // If not draft, redirect to detail — no editing allowed here
        if (loadedPo.status !== 'draft') {
          navigate(detailPath(poId), { replace: true });
        }
      })
      .catch(err => {
        setInitError(err?.response?.data?.message ?? err?.message ?? 'Failed to load purchase order');
      })
      .finally(() => setInitLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreate, poId]);

  // ── Header save ───────────────────────────────────────────────────────────────
  const handleCreateOrSave = async () => {
    setHeaderTouched(true);
    if (!supplierId) return;

    setHeaderSaving(true);
    setHeaderError(null);
    setHeaderSuccess(false);

    try {
      if (isCreate) {
        const created = await purchaseOrderService.createOrder({
          supplierId,
          venueId,
          ...(expectedDeliveryDate && { expectedDeliveryDate }),
          ...(notes.trim() && { notes: notes.trim() }),
        });
        navigate(editPath(created._id), { replace: true });
      } else if (poId) {
        const updated = await purchaseOrderService.updateOrder(poId, {
          expectedDeliveryDate: expectedDeliveryDate || null,
          notes: notes || undefined,
        });
        setPo(updated);
        setHeaderSuccess(true);
        setTimeout(() => setHeaderSuccess(false), 3000);
      }
    } catch (err: any) {
      setHeaderError(err?.response?.data?.message ?? err?.message ?? 'Save failed');
    } finally {
      setHeaderSaving(false);
    }
  };

  // ── Line editing ──────────────────────────────────────────────────────────────
  const startEditLine = (line: PurchaseOrderLine) => {
    setEditingLineId(line._id);
    setEditQty(String(line.orderedQty));
    // W11 — prefill the editor with the stored unitPrice if any, otherwise
    // fall back to the backend-suggested value (supplier-link cost). The
    // user can still wipe it; clicking Save commits whatever's in the box.
    const seed = line.unitPrice ?? line.suggestedUnitPrice ?? null;
    setEditPrice(seed != null ? String(seed) : '');
    setLineOpError(null);
  };

  const cancelEditLine = () => {
    setEditingLineId(null);
    setLineOpError(null);
  };

  const saveEditLine = async (lineId: string) => {
    if (!poId) return;
    const qtyNum = parseInt(editQty, 10);
    if (isNaN(qtyNum) || qtyNum < 1) {
      setLineOpError('Quantity must be a positive integer.');
      return;
    }
    setLineOpLoading(true);
    setLineOpError(null);
    try {
      const priceNum = editPrice !== '' ? parseFloat(editPrice) : undefined;
      await purchaseOrderService.updateLine(poId, lineId, {
        orderedQty: qtyNum,
        ...(priceNum !== undefined && { unitPrice: priceNum }),
      });
      await loadLines(poId);
      setEditingLineId(null);
    } catch (err: any) {
      setLineOpError(err?.response?.data?.message ?? err?.message ?? 'Failed to update line');
    } finally {
      setLineOpLoading(false);
    }
  };

  const handleDeleteLine = async (lineId: string, displayName: string) => {
    if (!poId) return;
    if (!window.confirm(`Remove "${displayName}" from this order?`)) return;
    setLineOpLoading(true);
    setLineOpError(null);
    try {
      await purchaseOrderService.deleteLine(poId, lineId);
      await loadLines(poId);
    } catch (err: any) {
      setLineOpError(err?.response?.data?.message ?? err?.message ?? 'Failed to remove line');
    } finally {
      setLineOpLoading(false);
    }
  };

  const locationName = (locationId?: string) =>
    locationId ? (locations.find(l => l._id === locationId)?.name ?? '—') : '—';

  // ── Render: loading / error ────────────────────────────────────────────────────
  if (initLoading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', pt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (initError) {
    return (
      <Box sx={{ p: 3 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate(listPath)} sx={{ mb: 2, textTransform: 'none', color: 'text.secondary' }}>
          Purchase Orders
        </Button>
        <Alert severity="error">{initError}</Alert>
      </Box>
    );
  }

  const supplierError = headerTouched && !supplierId;

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {/* Back nav */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate(poId ? detailPath(poId) : listPath)}
          sx={{ textTransform: 'none', color: 'text.secondary' }}
        >
          {poId ? 'Purchase Order' : 'Purchase Orders'}
        </Button>
        <Divider orientation="vertical" flexItem sx={{ my: 0.5 }} />
        <Typography variant="h6" fontWeight={700}>
          {isCreate ? 'New Purchase Order' : 'Edit Draft'}
        </Typography>
      </Box>

      {/* Header form */}
      <Paper sx={{ p: 3, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          Order Details
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1.5 }}>
          {/* Supplier */}
          <FormControl fullWidth error={supplierError} required>
            <InputLabel>Supplier</InputLabel>
            <Select
              label="Supplier"
              value={supplierId}
              onChange={e => setSupplierId(e.target.value)}
              disabled={!isCreate && !!po}
            >
              {suppliers.length === 0 && (
                <MenuItem disabled value="">No suppliers available</MenuItem>
              )}
              {suppliers.map(s => (
                <MenuItem key={s._id} value={s._id}>{s.name}</MenuItem>
              ))}
            </Select>
            {supplierError && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                Supplier is required
              </Typography>
            )}
            {!isCreate && po && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.75 }}>
                Supplier cannot be changed after the order is created
              </Typography>
            )}
          </FormControl>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              label="Expected Delivery"
              type="date"
              value={expectedDeliveryDate}
              onChange={e => setExpectedDeliveryDate(e.target.value)}
              size="small"
              sx={{ minWidth: 200 }}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <Box sx={{ flex: 1, minWidth: 260 }}>
              <TextField
                label="Notes"
                value={notes}
                onChange={e => setNotes(e.target.value)}
                multiline
                rows={2}
                fullWidth
                size="small"
                placeholder="Optional — delivery instructions, references, etc."
              />
            </Box>
          </Box>

          {headerError && <Alert severity="error" onClose={() => setHeaderError(null)}>{headerError}</Alert>}
          {headerSuccess && <Alert severity="success">Header saved.</Alert>}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
            <Button onClick={() => navigate(poId ? detailPath(poId) : listPath)} sx={{ textTransform: 'none' }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCreateOrSave}
              disabled={headerSaving}
              startIcon={headerSaving ? <CircularProgress size={16} /> : undefined}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {isCreate ? 'Create Draft' : 'Save Changes'}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Lines section — only shown in edit mode */}
      {!isCreate && poId && (
        <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
          <Box
            sx={{
              px: 3, py: 2,
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}
          >
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>Order Lines</Typography>
              <Typography variant="caption" color="text.secondary">
                {lines.length} {lines.length === 1 ? 'line' : 'lines'}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setAddDialogOpen(true)}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Add Line
            </Button>
          </Box>

          {lineOpError && (
            <Box sx={{ px: 3, pt: 2 }}>
              <Alert severity="error" onClose={() => setLineOpError(null)}>{lineOpError}</Alert>
            </Box>
          )}

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Item', 'Size', 'Location', 'Qty', 'Unit Price', ''].map(h => (
                    <TableCell
                      key={h}
                      sx={{ fontWeight: 600, color: theme.palette.text.secondary, fontSize: '0.7rem', textTransform: 'uppercase', py: 1.25 }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {lines.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Box sx={{ py: 5, textAlign: 'center', color: theme.palette.text.secondary }}>
                        <Typography variant="body2">No lines yet. Click "Add Line" to search your inventory.</Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  lines.map(line => {
                    const isEditing = editingLineId === line._id;
                    return (
                      <TableRow
                        key={line._id}
                        sx={isEditing ? { backgroundColor: alpha(theme.palette.primary.main, 0.04) } : undefined}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>{line.displayName}</Typography>
                          {line.supplierItemCode && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              {line.supplierItemCode}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {formatPoLineSize(line) || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">{line.locationName || '—'}</Typography>
                        </TableCell>
                        <TableCell sx={{ minWidth: 90 }}>
                          {isEditing ? (
                            <TextField
                              value={editQty}
                              onChange={e => setEditQty(e.target.value)}
                              type="number"
                              size="small"
                              sx={{ width: 72 }}
                              slotProps={{ htmlInput: { min: 1, step: 1 } }}
                            />
                          ) : (
                            <Typography variant="body2">{line.orderedQty}</Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ minWidth: 110 }}>
                          {isEditing ? (
                            <TextField
                              value={editPrice}
                              onChange={e => setEditPrice(e.target.value)}
                              type="number"
                              size="small"
                              sx={{ width: 90 }}
                              placeholder="—"
                              slotProps={{ htmlInput: { min: 0, step: 0.01 } }}
                            />
                          ) : line.unitPrice != null ? (
                            <Typography variant="body2" color="text.secondary">
                              {`${defaultCurrency}${line.unitPrice.toFixed(2)}`}
                            </Typography>
                          ) : line.suggestedUnitPrice != null ? (
                            // W11 — show the supplier-cost suggestion in
                            // muted italic; clicking Edit will prefill it.
                            <Typography
                              variant="body2"
                              sx={{ color: 'text.disabled', fontStyle: 'italic' }}
                              title="Suggested from supplier cost. Edit the line to commit."
                            >
                              {`${defaultCurrency}${line.suggestedUnitPrice.toFixed(2)}`}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.secondary">—</Typography>
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          {isEditing ? (
                            <>
                              <Tooltip title="Save">
                                <span>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => saveEditLine(line._id)}
                                    disabled={lineOpLoading}
                                  >
                                    {lineOpLoading ? <CircularProgress size={16} /> : <SaveIcon fontSize="small" />}
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Cancel">
                                <IconButton size="small" onClick={cancelEditLine} disabled={lineOpLoading}>
                                  <CancelIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          ) : (
                            <>
                              <Tooltip title="Edit">
                                <IconButton size="small" onClick={() => startEditLine(line)}>
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Remove">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeleteLine(line._id, line.displayName)}
                                  disabled={lineOpLoading}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Add Line Dialog */}
      {!isCreate && poId && (
        <AddLineDialog
          open={addDialogOpen}
          onClose={() => setAddDialogOpen(false)}
          onAdded={() => loadLines(poId)}
          poId={poId}
          venueId={venueId}
          supplierId={supplierId}
          locations={locations}
          existingInventoryItemIds={existingInventoryItemIds}
          supplierCodeMap={supplierCodeMap}
        />
      )}
    </Box>
  );
};

export default PurchaseOrderForm;
